import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface DeviceAssemblyRow {
  id: string;
  serial_number: string;
  device_id: string;
  pcb_serial_number: string;
  build_month: string;
  batch_id: number;
  created_at: string;
  updated_at: string;
  led_pcb_serial_number: string | null;
  built_by: string | null;
  checked_by: string | null;
  qc_night_camera_end: boolean;
  qc_day_camera_center: boolean;
  qc_lens_covers_removed: boolean;
  qc_programming_complete: boolean;
  qc_switch_position: boolean;
  completed_at: string | null;
  completed_by: string | null;
}

export const currentBuildMonth = (d: Date = new Date()) => {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${mm}-${yy}`;
};

export const useDeviceAssembly = () => {
  return useQuery({
    queryKey: ["device-assembly"],
    queryFn: async (): Promise<DeviceAssemblyRow[]> => {
      const { data, error } = await supabase
        .from("device_assembly" as any)
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data as unknown as DeviceAssemblyRow[]) || [];
    },
  });
};

export const useCreateAssemblyDevice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      serialNumber: string;
      particleSerialNumber: string;
      pcbSerialNumber: string;
      buildMonth: string;
      batchId: number;
    }) => {
      const { data: fnData, error: fnError } = await supabase.functions.invoke(
        "assign-device-serial",
        {
          body: {
            serialNumber: args.serialNumber,
            particleSerialNumber: args.particleSerialNumber,
          },
        }
      );
      if (fnError) {
        // Surface the function's actual JSON error body instead of the generic
        // "Edge Function returned a non-2xx status code" message.
        let detail = "";
        const ctx: any = (fnError as any).context;
        try {
          if (ctx?.json) {
            const body = await ctx.json();
            detail = body?.error || body?.message || "";
          } else if (ctx?.text) {
            detail = await ctx.text();
          }
        } catch {
          /* ignore body parse failures */
        }
        throw new Error(detail || fnError.message || "BuildShip API call failed");
      }
      if (!fnData || fnData.success !== true || !fnData.deviceId || !fnData.name) {
        throw new Error(fnData?.error || "BuildShip did not return a valid device");
      }

      const { data: session } = await supabase.auth.getSession();

      const { data: inserted, error: insertErr } = await supabase
        .from("device_assembly" as any)
        .insert({
          serial_number: fnData.name,
          device_id: fnData.deviceId,
          pcb_serial_number: args.pcbSerialNumber,
          build_month: args.buildMonth,
          batch_id: args.batchId,
          created_by: session.session?.user.id ?? null,
        })
        .select()
        .single();
      if (insertErr) throw insertErr;
      return inserted as unknown as DeviceAssemblyRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["device-assembly"] });
    },
  });
};

export type AssemblyUpdatePatch = Partial<{
  led_pcb_serial_number: string | null;
  built_by: string | null;
  checked_by: string | null;
  qc_night_camera_end: boolean;
  qc_day_camera_center: boolean;
  qc_lens_covers_removed: boolean;
  qc_programming_complete: boolean;
  qc_switch_position: boolean;
  completed_at: string | null;
  completed_by: string | null;
}>;

export const useUpdateAssemblyDevice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: AssemblyUpdatePatch }) => {
      const { data, error } = await supabase
        .from("device_assembly" as any)
        .update(patch)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as unknown as DeviceAssemblyRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["device-assembly"] });
    },
  });
};

export const useDeleteAssemblyDevice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("device_assembly" as any)
        .delete()
        .eq("id", id);
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["device-assembly"] });
    },
  });
};